/**
 * Ask an image host for the size we are actually going to display.
 *
 * Two sources, two mechanisms.
 *
 * TCGdex serves each card at /high.webp and /low.webp off the same path, about
 * 76 KB against 17 KB. The small file is roughly 245px wide, so it only suits
 * grid tiles.
 *
 * User photos live in Supabase storage and come back at whatever the camera
 * produced. In this project that averages 751 KB and reaches 3.1 MB, to fill a
 * frame the size of a playing card. Supabase resizes on request, and since
 * browsers send an Accept header asking for WebP, a 2.3 MB scan comes back at
 * 57 KB. Forty times smaller for a change to the URL, with no re-upload.
 *
 * Anything hosted elsewhere is returned untouched, as is a request for width 0,
 * which is how a zoomable full-screen view asks for the real thing.
 */

const STORAGE_OBJECT = '/storage/v1/object/public/';
const STORAGE_RENDER = '/storage/v1/render/image/public/';

/** Widths worth naming, so call sites read as intent rather than arithmetic. */
export const IMG = {
  // The fallback for a grid tile that could not be measured - jsdom, or a
  // container with no laid-out width yet. Deliberately generous: an oversized
  // tile is slow, an undersized one is soft, and this is the case where nothing
  // is known about the surface.
  TILE: 350,
  STRIP: 160,  // small thumbnail strips, ~70pt CSS at 2x
  CARD: 900,   // a card filling most of a phone screen
  FULL: 0,     // untouched original, for pinch-zoom
};

/**
 * The only widths this app will ever ask storage for.
 *
 * Every distinct width is a separate object the render endpoint has to generate
 * and then cache on its own, and the first request for a size it has never seen
 * is the slow one. Asking for the exact device-pixel width of a tile would give
 * a 393pt phone a private set of renders that no other phone ever reuses, so
 * each surface snaps up to the nearest rung and the whole install base shares
 * the same handful of cached sizes.
 *
 * 350 and 900 are on the ladder because they are the two widths this app has
 * been asking for all along, so much of the catalogue is already generated at
 * both and a two-across tile and a card detail cost nothing new.
 *
 * The rungs are placed where the phones are. A three-across catalogue tile is
 * 109pt on an SE, 114pt on a 390pt phone and 127pt on a Pro Max, which at two
 * device pixels each is 218, 228 and 254 - so 220 and 280 catch every one of
 * them and none of them falls through to 350, which was 27% more bytes than the
 * biggest of those tiles could show.
 */
export const WIDTH_LADDER = [160, 220, 280, 350, 450, 640, 900];

/**
 * The rung to ask for, given how wide the tile actually rendered.
 *
 * Device pixel ratio is deliberately capped at 2. Card art in a grid tile is
 * already down to the point where the number is unreadable, and the third
 * pixel per point roughly doubles the bytes for detail nobody is looking at on
 * a 70pt thumbnail. Full-bleed views are not thumbnails and get the top rung.
 *
 * @param {number} cssWidth rendered width of the tile in CSS pixels
 * @param {number} [dpr] device pixel ratio
 * @returns {number} a width from WIDTH_LADDER
 */
export function ladderWidth(cssWidth, dpr = 1) {
  const wanted = Math.round(cssWidth * Math.min(dpr || 1, 2));
  return WIDTH_LADDER.find((w) => w >= wanted) || WIDTH_LADDER[WIDTH_LADDER.length - 1];
}

export function sizedImageUrl(url, width) {
  if (!url || typeof url !== 'string' || !width) return url;

  if (url.includes('assets.tcgdex.net')) {
    return width <= 500
      ? url.replace(/\/high\.(webp|png|jpg)(\?.*)?$/i, '/low.$1$2')
      : url;
  }

  // Catalogue art has a thumbnail stored beside it: card-art/thumb/<hash>.webp,
  // 350 wide, made once by scripts/prime-thumbs.mjs. A plain object off the CDN
  // is a fraction of the time of the render endpoint, which has to resize and
  // re-encode (or find its cache) on every request, and on a phone at a card
  // show that was most of what a grid was waiting on. A tile that finds no
  // thumbnail there yet falls back to the render URL through CardImage's
  // candidate chain, so a card mirrored five minutes ago still shows.
  if (url.includes(`${STORAGE_OBJECT}card-art/thumb/`)) return url;
  if (width <= 500 && url.includes(`${STORAGE_OBJECT}card-art/`)) {
    return url.replace(`${STORAGE_OBJECT}card-art/`, `${STORAGE_OBJECT}card-art/thumb/`).replace(/\.(png|jpe?g)(\?.*)?$/i, '.webp$2');
  }

  if (url.includes(STORAGE_OBJECT)) {
    const rendered = url.replace(STORAGE_OBJECT, STORAGE_RENDER);
    const sep = rendered.includes('?') ? '&' : '?';
    // resize=contain is not optional. Width on its own does NOT preserve the
    // aspect ratio - it rewrites the width and leaves the height alone, so a
    // 2722x3683 card photo comes back as 900x3683 and renders as a thin strip.
    return `${rendered}${sep}width=${width}&resize=contain&quality=${width <= 500 ? 65 : 78}`;
  }

  return url;
}

/**
 * A second source for card art, derived from a TCGdex URL.
 *
 * TCGdex is the primary catalogue and is nearly complete, but it does have
 * holes: 67 of the 248 cards in SM Black Star Promos carry no artwork at all,
 * Dragonite GX SM156 among them. The card record exists, the image simply does
 * not, and the URL we build for it returns a 404 with no way to tell that apart
 * from a network failure.
 *
 * The set id and card number are already in the TCGdex path, and pokemontcg.io
 * addresses its images with the same two values, so a second candidate costs
 * nothing to derive and no extra lookup. The set ids agree for most sets and
 * not for all; where they disagree this 404s as well and the card falls through
 * to the owner's own photo, which is the same place it would have landed.
 *
 * @param {string} url a TCGdex asset URL
 * @returns {string|null}
 */
export function altArtUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (!url.includes('assets.tcgdex.net')) return null;

  // .../<lang>/<series>/<setId>/<localId>/<quality>.<ext>
  const m = url.match(/assets\.tcgdex\.net\/([a-z]{2})\/[^/]+\/([^/]+)\/([^/]+)\/(?:high|low)\.\w+$/i);
  if (!m) return null;

  const [, lang, setId, localId] = m;
  // The alternate catalogue is English only.
  if (lang !== 'en') return null;

  return `https://images.pokemontcg.io/${setId}/${localId}.png`;
}

/**
 * The render-endpoint URL for a catalogue thumbnail, for when the stored one
 * is not there. Null for anything that is not a card-art thumbnail.
 */
export function thumbFallbackUrl(url, width) {
  if (!url || typeof url !== 'string' || !url.includes(`${STORAGE_OBJECT}card-art/thumb/`)) return null;
  const original = url.replace(`${STORAGE_OBJECT}card-art/thumb/`, `${STORAGE_RENDER}card-art/`);
  const sep = original.includes('?') ? '&' : '?';
  return `${original}${sep}width=${width || IMG.TILE}&resize=contain&quality=65`;
}

export default { sizedImageUrl, altArtUrl, thumbFallbackUrl, IMG };
